/**
 * Inventory theme — derived from shared IPD design tokens.
 * Colors: src/pages/ipd/utils/ipdColors.js
 */
import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdPageSx,
  ipdCardSx,
  formRowBg,
  formRowSx,
  switchSx,
} from '../ipd/utils/ipdColors';
import {
  ipdElevatedInputSx,
  getIpdSelectMenuProps,
} from '../ipd/utils/ipdFieldStyles';
import { IPD_SIDEBAR } from '../ipd/utils/ipdSidebar';
import { tableContainerSx } from '../ipd/utils/ipdTable';
import { hideScrollbarSx, thinScrollbarSx } from '../ipd/utils/ipdScrollbar';
import { FILTER_TOOLBAR, toolbarSx, filterToggleSx } from '../ipd/utils/ipdToolbar';
import { menuPaperSx } from '../ipd/utils/ipdMenu';

export { IPD_COLORS, IPD_SHAPE, ipdPageSx, ipdCardSx };
export { createIpdTheme } from '../ipd/utils/ipdTheme';
export { getIpdSelectMenuProps };
export { IPD_SIDEBAR };
export { tableContainerSx };
export { hideScrollbarSx, thinScrollbarSx };
export { FILTER_TOOLBAR, toolbarSx, filterToggleSx };
export { menuPaperSx };
export { formRowBg, formRowSx };
export { switchSx };

/** Extra chart / badge hues not in IPD_COLORS */
export const INV_CHART = {
  purple: '#6f42c1',
};

/**
 * Inventory-local scales.
 *
 * IPD ships IPD_SPACING / IPD_TYPOGRAPHY, but they are unused here and their values
 * disagree with what the module actually renders (pageTitle says 20px, PageHeader
 * ships 22/26). IPD is shared with the rest of the app, so rather than change it we
 * define INV_* equivalents calibrated to the real rendered values. Radii still come
 * from IPD_SHAPE where a matching step exists.
 */
export const INV_RADIUS = {
  chip: IPD_SHAPE.radiusSm,     // 6  — status chips
  card: IPD_SHAPE.radiusMd,     // 8  — cards, panels, grids
  control: IPD_SHAPE.radiusLg,  // 10 — buttons, inputs, icon tiles
  tile: IPD_SHAPE.radiusXl,     // 12 — quick-action tiles
  dialog: 14,                   // no IPD step; already the de-facto dialog radius
};

export const INV_SPACING = {
  page: 3,
  section: 2.5,
  card: 2.5,
  grid: 2,
  stack: 1.5,
};

export const INV_TYPE = {
  pageTitle: { fontWeight: 700, fontSize: { xs: 22, sm: 26 }, lineHeight: 1.2, letterSpacing: '-0.02em' },
  sectionTitle: { fontWeight: 600, fontSize: 15 },
  cardTitle: { fontWeight: 700, fontSize: 15, lineHeight: 1.3 },
  body: { fontSize: 14, lineHeight: 1.5 },
  caption: { fontSize: 12, lineHeight: 1.4 },
  statLabel: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1.3 },
  statValue: { fontSize: 32, fontWeight: 700, lineHeight: 1 },
  chip: { fontSize: 11, fontWeight: 600, letterSpacing: 0.3 },
};

/**
 * The only sanctioned alpha suffixes for tinting a hex color. The module had eleven
 * ad-hoc levels (12/14/16/18/22/30/33/44/50/55/66); these three cover every case.
 */
export const INV_ALPHA = {
  tint: '18',    // soft background
  border: '55',  // soft border
  row: '12',     // data-grid row wash
};

export const cardShadow = IPD_COLORS.shadow.card;
export const cardShadowHover = IPD_COLORS.shadow.cardHover;

export const cardSx = {
  ...ipdCardSx,
  color: IPD_COLORS.textPrimary,
};

export const pageSx = {
  ...ipdPageSx,
  minHeight: '100vh',
  color: IPD_COLORS.textPrimary,
};

export const inputSx = ipdElevatedInputSx;

/**
 * @deprecated Use IPD_COLORS — kept so stale imports still compile during migration.
 */
export const INV_COLORS = {
  pageBg: IPD_COLORS.pageBg,
  cardBg: IPD_COLORS.cardBg,
  primary: IPD_COLORS.primary,
  primaryHover: IPD_COLORS.primaryDark,
  border: IPD_COLORS.border,
  borderLight: IPD_COLORS.border,
  accent: IPD_COLORS.selectedBg,
  teal: IPD_COLORS.primary,
  tealLight: IPD_COLORS.primaryLight,
  tealDark: IPD_COLORS.primaryDark,
  text: IPD_COLORS.textPrimary,
  textMuted: IPD_COLORS.textSecondary,
  slate: IPD_COLORS.textSecondary,
  slateDark: IPD_COLORS.textPrimary,
  success: IPD_COLORS.success.text,
  emerald: IPD_COLORS.success.text,
  mint: IPD_COLORS.success.text,
  warning: IPD_COLORS.warning.text,
  amber: IPD_COLORS.warning.text,
  amberDark: IPD_COLORS.warning.text,
  red: IPD_COLORS.error.text,
  danger: IPD_COLORS.error.text,
  blue: IPD_COLORS.info.text,
  purple: INV_CHART.purple,
};
